import React, { useState, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery, Popper } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Clear, MailOutline, PhoneAndroid } from "@material-ui/icons";
import { URL as url } from "../../config/config";
import dialCodes from "../../config/dialCodes";

const RESEND_COOLDOWN = 60;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const DEFAULT_DIAL_CODE = { name: "India", dialCode: "+91", isoCode: "IN" };

function DialCodePopper({ style, ...props }) {
  return <Popper {...props} style={{ ...style, width: 280, maxWidth: "90vw" }} placement="bottom-start" />;
}

// ── Channel card ─────────────────────────────────────────────────────────────

function ChannelCard({ icon, label, masked, selected, disabled, onClick, primaryColor }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        border: `1.5px solid ${selected ? primaryColor : "#e5e7eb"}`,
        borderRadius: 12,
        backgroundColor: selected ? `${primaryColor}0d` : "#fff",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "border-color 0.15s, background-color 0.15s",
        userSelect: "none",
      }}
    >
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          backgroundColor: selected ? `${primaryColor}1a` : "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { style: { fontSize: 20, color: selected ? primaryColor : "#6b7280" } })}
      </Box>
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Typography style={{ fontSize: 13, fontWeight: 600, color: selected ? primaryColor : "#374151", lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography noWrap style={{ fontSize: 12, color: "#9ca3af", fontFamily: "monospace", letterSpacing: 1 }}>
          {masked}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function ChangeContactDialog({
  open,
  onClose,
  purpose,
  currentEmail,
  currentPhone,
  onSuccess,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isEmailChange = purpose === "email_change";

  const defaultChannel = currentEmail && currentPhone ? null : (currentEmail ? "email" : "phone");

  // Step 1
  const [channel, setChannel] = useState(defaultChannel);
  const [identityOtpSent, setIdentityOtpSent] = useState(false);
  const [identityOtp, setIdentityOtp] = useState("");
  const [identityResendTimer, setIdentityResendTimer] = useState(0);
  const [identityToken, setIdentityToken] = useState("");

  // Step 2
  const [step, setStep] = useState(1);
  const [newValue, setNewValue] = useState("");           // email
  const [selectedDialCode, setSelectedDialCode] = useState(DEFAULT_DIAL_CODE); // phone
  const [localNumber, setLocalNumber] = useState("");     // phone
  const [changeOtpSent, setChangeOtpSent] = useState(false);
  const [changeOtp, setChangeOtp] = useState("");
  const [changeResendTimer, setChangeResendTimer] = useState(0);

  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const identityTimerRef = useRef(null);
  const changeTimerRef = useRef(null);

  const startTimer = (setTimer, timerRef) => {
    clearInterval(timerRef.current);
    setTimer(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleClose = () => {
    clearInterval(identityTimerRef.current);
    clearInterval(changeTimerRef.current);
    setStep(1);
    setChannel(currentEmail && currentPhone ? null : (currentEmail ? "email" : "phone"));
    setIdentityOtpSent(false);
    setIdentityOtp("");
    setIdentityResendTimer(0);
    setIdentityToken("");
    setNewValue("");
    setSelectedDialCode(DEFAULT_DIAL_CODE);
    setLocalNumber("");
    setChangeOtpSent(false);
    setChangeOtp("");
    setChangeResendTimer(0);
    setLoading(false);
    setError("");
    onClose();
  };

  // ── Step 1 ──────────────────────────────────────────────────────────────────

  const handleSendIdentityOtp = async () => {
    setLoading(true);
    setError("");
    setIdentityOtp("");
    try {
      const endpoint = isEmailChange ? "/auth/send-identity-email-otp" : "/auth/send-identity-phone-otp";
      await axios.post(url + endpoint, { channel });
      setIdentityOtpSent(true);
      startTimer(setIdentityResendTimer, identityTimerRef);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyIdentityOtp = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(url + "/auth/verify-identity-otp", { otp: identityOtp });
      setIdentityToken(res.data.identityToken);
      clearInterval(identityTimerRef.current);
      setStep(2);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid or expired OTP.");
      setIdentityOtp("");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 ──────────────────────────────────────────────────────────────────

  const combinedPhone = `${selectedDialCode.dialCode.replace("+", "")}${localNumber}`;

  const handleSendChangeOtp = async () => {
    setLoading(true);
    setError("");
    setChangeOtp("");
    try {
      const endpoint = isEmailChange ? "/auth/request-email-change" : "/auth/request-phone-change";
      const body = isEmailChange
        ? { newEmail: newValue, identityToken }
        : { newPhone: combinedPhone, isoCode: selectedDialCode.isoCode, identityToken };
      await axios.post(url + endpoint, body);
      setChangeOtpSent(true);
      startTimer(setChangeResendTimer, changeTimerRef);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyChangeOtp = async () => {
    setLoading(true);
    setError("");
    try {
      const endpoint = isEmailChange ? "/auth/verify-email-change-otp" : "/auth/verify-phone-change-otp";
      await axios.post(url + endpoint, { otp: changeOtp });
      clearInterval(changeTimerRef.current);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Verification failed.");
      setChangeOtp("");
    } finally {
      setLoading(false);
    }
  };

  const isNewValueValid = isEmailChange
    ? isValidEmail(newValue)
    : (selectedDialCode !== null && /^\d{6,12}$/.test(localNumber));
  const hasEmail = !!currentEmail;
  const hasPhone = !!currentPhone;
  const stepLabel = `Step ${step} of 2`;
  const stepTitle = step === 1
    ? "Verify your identity"
    : isEmailChange ? "Enter new email address" : "Enter new phone number";
  const stepSubtitle = step === 1
    ? "For security, confirm it's you before making changes."
    : isEmailChange
      ? "We'll send an OTP to verify your new email address."
      : "We'll send an OTP to verify your new number.";

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
      PaperProps={{ style: { margin: isMobile ? 16 : 32 } }}
    >
      {/* Header */}
      <Box style={{ padding: isMobile ? "20px 20px 0 20px" : "24px 28px 0 24px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>
              {stepLabel}
            </Typography>
            <Typography variant="h3" style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>
              {stepTitle}
            </Typography>
            <Typography variant="body2" style={{ color: "#6b7280" }}>
              {stepSubtitle}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" style={{ marginTop: -4 }}>
            <Clear fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent style={{ padding: isMobile ? "16px 20px" : "20px 28px 20px 24px" }}>

        {/* ── Step 1 ── */}
        {step === 1 && (
          <>
            {/* No contact on file — show error and nothing else */}
            {!hasEmail && !hasPhone && (
              <Typography variant="body2" style={{ color: "#dc2626", padding: "8px 0" }}>
                No contact information on file. Please contact support to update your email or phone before making changes.
              </Typography>
            )}

            {/* Channel cards — only shown if that contact exists */}
            {(hasEmail || hasPhone) && (
              <Box display="flex" style={{ gap: 12, marginBottom: 20, flexDirection: isMobile ? "column" : "row" }}>
                {hasEmail && (
                  <ChannelCard
                    icon={<MailOutline />}
                    label="Email"
                    masked={currentEmail}
                    selected={channel === "email"}
                    disabled={identityOtpSent}
                    onClick={() => setChannel("email")}
                    primaryColor={primaryColor}
                  />
                )}
                {hasPhone && (
                  <ChannelCard
                    icon={<PhoneAndroid />}
                    label="Phone"
                    masked={currentPhone}
                    selected={channel === "phone"}
                    disabled={identityOtpSent}
                    onClick={() => setChannel("phone")}
                    primaryColor={primaryColor}
                  />
                )}
              </Box>
            )}

            {/* OTP section — appears after Send OTP */}
            {identityOtpSent && (
              <Box>
                <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 8 }}>
                  Enter the 6-digit code sent to{" "}
                  <strong>
                    {channel === "email" ? currentEmail : currentPhone}
                  </strong>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter 6-digit OTP"
                  value={identityOtp}
                  onChange={(e) => setIdentityOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  fullWidth
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                />
              </Box>
            )}
          </>
        )}

        {/* ── Step 2 ── */}
        {step === 2 && (
          <>
            {isEmailChange ? (
              <TextField
                variant="outlined"
                type="email"
                placeholder="Enter new email address"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={changeOtpSent}
                fullWidth
                style={{ marginBottom: changeOtpSent ? 16 : 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MailOutline style={{ color: "#9ca3af", fontSize: 20 }} /></InputAdornment>,
                }}
              />
            ) : (
              <Box display="flex" style={{ gap: 8, marginBottom: changeOtpSent ? 16 : 0, flexDirection: isMobile ? "column" : "row" }}>
                <Autocomplete
                  options={dialCodes}
                  value={selectedDialCode}
                  onChange={(_, val) => val && setSelectedDialCode(val)}
                  getOptionLabel={(opt) => opt.dialCode}
                  getOptionSelected={(opt, val) => opt.isoCode === val.isoCode}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(opt =>
                      opt.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                      opt.dialCode.includes(inputValue)
                    )
                  }
                  renderOption={(opt) => (
                    <span style={{ whiteSpace: "nowrap" }}>{opt.dialCode}&nbsp;&nbsp;{opt.name}</span>
                  )}
                  PopperComponent={DialCodePopper}
                  disableClearable
                  disabled={changeOtpSent}
                  style={{ width: isMobile ? "100%" : 150 }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" margin="dense" placeholder="+91" />
                  )}
                />
                <TextField
                  variant="outlined"
                  margin="dense"
                  type="tel"
                  placeholder="Phone number"
                  value={localNumber}
                  onChange={(e) => setLocalNumber(e.target.value.replace(/\D/g, ""))}
                  disabled={changeOtpSent}
                  style={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneAndroid style={{ color: "#9ca3af", fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Box>
            )}

            {changeOtpSent && (
              <Box>
                <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 8 }}>
                  Enter the 6-digit code sent to{" "}
                  <strong>
                    {isEmailChange ? newValue : `${selectedDialCode.dialCode} ${localNumber}`}
                  </strong>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter 6-digit OTP"
                  value={changeOtp}
                  onChange={(e) => setChangeOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  fullWidth
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                />
              </Box>
            )}
          </>
        )}

        {/* Error */}
        {error && (
          <Typography variant="body2" style={{ color: "#dc2626", marginTop: 10, fontSize: 13 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ padding: isMobile ? "0 20px 20px" : "0 28px 24px", gap: 8 }}>
        {/* Step 1 — before OTP sent */}
        {step === 1 && !identityOtpSent && (
          <>
            <Button variant="outlined" size="small" onClick={handleClose}>Cancel</Button>
            {(hasEmail || hasPhone) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSendIdentityOtp}
                disabled={loading || !channel}
              >
                {loading ? <CircularProgress size={16} /> : "Send OTP"}
              </Button>
            )}
          </>
        )}

        {/* Step 1 — after OTP sent */}
        {step === 1 && identityOtpSent && (
          <>
            <Button variant="outlined" size="small" onClick={handleClose}>Cancel</Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSendIdentityOtp}
              disabled={loading || identityResendTimer > 0}
            >
              {identityResendTimer > 0 ? `Resend (${identityResendTimer}s)` : "Resend"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleVerifyIdentityOtp}
              disabled={loading || identityOtp.length !== 6}
            >
              {loading ? <CircularProgress size={16} /> : "Verify"}
            </Button>
          </>
        )}

        {/* Step 2 — before OTP sent */}
        {step === 2 && !changeOtpSent && (
          <>
            <Button variant="outlined" size="small" onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSendChangeOtp}
              disabled={loading || !isNewValueValid}
            >
              {loading ? <CircularProgress size={16} /> : "Send OTP"}
            </Button>
          </>
        )}

        {/* Step 2 — after OTP sent */}
        {step === 2 && changeOtpSent && (
          <>
            <Button variant="outlined" size="small" onClick={handleClose}>Cancel</Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSendChangeOtp}
              disabled={loading || changeResendTimer > 0}
            >
              {changeResendTimer > 0 ? `Resend (${changeResendTimer}s)` : "Resend"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleVerifyChangeOtp}
              disabled={loading || changeOtp.length !== 6}
            >
              {loading ? <CircularProgress size={16} /> : "Verify & Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
